import type { ApiRequestConfig } from './api';
import { apiClient } from './api';

export type WebPreferredLandingMode = 'COMMUNITY' | 'COACH' | 'STRINGER' | 'ACADEMY' | 'WORKSPACE';

export interface WebUserProfile {
  firstName?: string | null;
  lastName?: string | null;
  profileName?: string | null;
  email?: string | null;
  emailVerified?: boolean;
  phoneNumber?: string | null;
  mobileVerified?: boolean;
  zipCode?: string | null;
  profilePicture?: string | null;
  visibility?: string | null;
  profileNameDaysRemaining?: number | null;
  preferredLandingMode?: WebPreferredLandingMode | null;
  ageRange?: string | null;
  gender?: string | null;
  sport?: string | null;
  handedness?: string | null;
  playStyle?: string | null;
  backhand?: string | null;
  ustaRank?: string | null;
  utrRank?: string | null;
  pickleballRank?: string | null;
  homeCourts?: WebProfileHomeCourt[];
}

export interface WebProfileHomeCourt {
  name?: string | null;
  type?: string | null;
}

export interface UpdateWebProfilePayload {
  firstName?: string;
  lastName?: string;
  zipCode?: string;
  ageRange?: string;
  gender?: string;
  sport?: string;
  handedness?: string;
  playStyle?: string;
  backhand?: string;
  ustaRank?: string;
  utrRank?: string;
  preferredLandingMode?: WebPreferredLandingMode;
}

export interface WebProfileNameCheckResponse {
  profileName: string;
  available: boolean;
  valid: boolean;
}

export interface WebProfileNameUpdateResponse {
  success: boolean;
  profileName?: string;
  error?: string;
  nextAllowedChangeAt?: string;
}

export interface WebEmailVerificationResponse {
  message?: string;
  status?: string;
  email?: string;
  emailSentTo?: string;
  error?: string;
}

export type WebDataExportStatus =
  | 'NONE'
  | 'PENDING'
  | 'PROCESSING'
  | 'READY'
  | 'FAILED'
  | 'EXPIRED';

export interface WebDataExportResponse {
  success: boolean;
  message: string;
  status: WebDataExportStatus;
  requestId: number | null;
  requestedAt: string | null;
  completedAt: string | null;
  expiresAt: string | null;
  canDownload: boolean;
  fileName: string | null;
}

export interface WebDataExportDownloadResponse {
  url: string;
  expiresAt: string;
  fileName: string;
}

export interface WebEmailPreferences {
  marketingEmails?: boolean;
  matchNotifications?: boolean;
  messageNotifications?: boolean;
  weeklyDigest?: boolean;
}

export interface WebPeopleDiscoverySettings {
  discoverableByEmail: boolean;
  discoverableByPhone: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
}

export interface UpdateWebPeopleDiscoverySettingsPayload {
  discoverableByEmail: boolean;
  discoverableByPhone: boolean;
}

export async function getWebProfile(): Promise<WebUserProfile> {
  const response = await apiClient.get<WebUserProfile>('/api/v1/web/user/profile');
  return response.data;
}

export async function updateWebProfile(payload: UpdateWebProfilePayload, config?: ApiRequestConfig): Promise<WebUserProfile> {
  const response = await apiClient.put<WebUserProfile>('/api/v1/web/user/profile', payload, config);
  return response.data;
}

export async function checkWebProfileName(profileName: string): Promise<WebProfileNameCheckResponse> {
  const response = await apiClient.get<WebProfileNameCheckResponse>('/api/v1/web/user/profile-name/check', {
    params: { profileName },
  });
  return response.data;
}

export async function updateWebProfileName(profileName: string, config?: ApiRequestConfig): Promise<WebProfileNameUpdateResponse> {
  const response = await apiClient.patch<WebProfileNameUpdateResponse>('/api/v1/web/user/profile-name', {
    profileName,
  }, config);
  return response.data;
}

export async function resendWebEmailVerification(): Promise<WebEmailVerificationResponse> {
  const response = await apiClient.post<WebEmailVerificationResponse>('/api/v1/web/user/resend-verification');
  return response.data;
}

export async function updateWebProfileVisibility(visibility: string, config?: ApiRequestConfig): Promise<WebUserProfile> {
  const response = await apiClient.put<WebUserProfile>('/api/v1/web/user/visibility', { visibility }, config);
  return response.data;
}

export async function requestWebDataExport(): Promise<WebDataExportResponse> {
  const response = await apiClient.post<WebDataExportResponse>('/api/v1/web/user/data-export/request');
  return response.data;
}

export async function getWebDataExportStatus(): Promise<WebDataExportResponse> {
  const response = await apiClient.get<WebDataExportResponse>('/api/v1/web/user/data-export/status');
  return response.data;
}

export async function createWebDataExportDownload(
  requestId: number
): Promise<WebDataExportDownloadResponse> {
  const response = await apiClient.post<WebDataExportDownloadResponse>(
    `/api/v1/web/user/data-export/${requestId}/download-link`
  );
  return response.data;
}

export async function getWebEmailPreferences(): Promise<WebEmailPreferences> {
  const response = await apiClient.get<WebEmailPreferences>('/api/v1/web/user/preferences/email');
  return response.data;
}

export async function updateWebEmailPreferences(preferences: WebEmailPreferences, config?: ApiRequestConfig): Promise<WebEmailPreferences> {
  const response = await apiClient.put<WebEmailPreferences>('/api/v1/web/user/preferences/email', preferences, config);
  return response.data;
}

export async function getWebPeopleDiscoverySettings(): Promise<WebPeopleDiscoverySettings> {
  const response = await apiClient.get<WebPeopleDiscoverySettings>('/api/v1/web/people-connections/settings');
  return response.data;
}

export async function updateWebPeopleDiscoverySettings(
  settings: UpdateWebPeopleDiscoverySettingsPayload, config?: ApiRequestConfig
): Promise<WebPeopleDiscoverySettings> {
  const response = await apiClient.put<WebPeopleDiscoverySettings>(
    '/api/v1/web/people-connections/settings',
    settings, config
  );
  return response.data;
}
